import {
  Injectable,
  computed,
  effect,
  inject,
  signal,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../auth/services/auth.service';
import { TokenService } from '../../auth/services/token.service';
import { AlertHistoryResponse } from '../models/notification.model';
import { NotificationApiService } from './notification-api.service';
import { NotificationRealtimeService } from './notification-realtime.service';

/**
 * Single source of truth for the in-app notifications feature.
 *
 * Responsibilities:
 *  - load the user's notifications + unread count via REST on authentication
 *  - keep them up to date in real time via STOMP/WebSocket
 *  - expose Signals (`notifications`, `unreadCount`) consumable by the navbar
 *  - persist read state through the REST API
 *
 * Each user only sees their own notifications: the backend enforces this via
 * the `userId` query param + JWT claim used as STOMP Principal.
 */
@Injectable({ providedIn: 'root' })
export class NotificationStore {
  private readonly api = inject(NotificationApiService);
  private readonly realtime = inject(NotificationRealtimeService);
  private readonly authService = inject(AuthService);
  private readonly tokenService = inject(TokenService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _notifications = signal<AlertHistoryResponse[]>([]);
  private readonly _unreadCount = signal<number>(0);
  private readonly _loading = signal<boolean>(false);

  readonly notifications = this._notifications.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly hasUnread = computed(() => this._unreadCount() > 0);

  constructor() {
    // Auto-connect/disconnect based on auth state. `effect` re-runs whenever
    // the authenticated signal changes (login, logout, token refresh).
    // We read userId from the JWT (TokenService) rather than authService.user()
    // because on cold-start with a cached token, AuthService.initializeAuth()
    // sets isAuthenticated=true WITHOUT hydrating the `_user` signal, so
    // authService.user() stays null. TokenService.getUserId() reads the `id`
    // claim directly from the access-token signal, which IS always populated.
    effect(() => {
      const authed = this.authService.isAuthenticated();
      // Track the access-token signal so the effect re-runs on login/refresh.
      const token = this.tokenService.accessToken();
      const userId = token ? this.tokenService.getUserId() : null;
      if (authed && userId) {
        this.initialize(userId);
      } else {
        this.teardown();
      }
    });

    // Wire realtime stream into the local state once.
    this.realtime.alerts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((alert) => this.onIncoming(alert));
  }

  /** Mark a single notification as read (optimistic). */
  markAsRead(id: string): void {
    const userId = this.tokenService.getUserId();
    if (!userId) return;

    const current = this._notifications();
    const target = current.find((n) => n.id === id);
    if (!target || target.read) return;

    this._notifications.set(
      current.map((n) =>
        n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n
      )
    );
    this._unreadCount.update((c) => Math.max(0, c - 1));

    this.api.markAsRead(id, userId).subscribe({
      error: () => this.refreshFromServer(userId), // rollback by reload
    });
  }

  /** Mark every notification as read. */
  markAllAsRead(): void {
    const userId = this.tokenService.getUserId();
    if (!userId) return;

    this._notifications.update((list) =>
      list.map((n) => ({ ...n, read: true }))
    );
    this._unreadCount.set(0);

    this.api.markAllAsRead(userId).subscribe({
      error: () => this.refreshFromServer(userId),
    });
  }

  /** Force a fresh load from the server. */
  refresh(): void {
    const userId = this.tokenService.getUserId();
    if (userId) this.refreshFromServer(userId);
  }

  // ───────────────────────── internals ─────────────────────────

  private initialize(userId: string): void {
    this.refreshFromServer(userId);
    this.realtime.connect();
  }

  private teardown(): void {
    this.realtime.disconnect();
    this._notifications.set([]);
    this._unreadCount.set(0);
  }

  private refreshFromServer(userId: string): void {
    this._loading.set(true);
    this.api.getNotifications(userId, 0, 20).subscribe({
      next: (page) => {
        this._notifications.set(page.content);
        this._loading.set(false);
      },
      error: () => this._loading.set(false),
    });
    this.api.countUnread(userId).subscribe({
      next: (count) => this._unreadCount.set(count),
    });
  }

  private onIncoming(alert: AlertHistoryResponse): void {
    const current = this._notifications();
    // Avoid duplicates if the same alert is delivered via /user and /topic.
    if (current.some((n) => n.id === alert.id)) return;

    // New alerts are unread on arrival; ensure flag and prepend.
    const normalized: AlertHistoryResponse = { ...alert, read: alert.read ?? false };
    this._notifications.set([normalized, ...current].slice(0, 50));
    if (!normalized.read) {
      this._unreadCount.update((c) => c + 1);
    }
  }
}
